import React, {useEffect} from "react";
import {useFormik} from "formik";
import {useTranslation} from "react-i18next";
import * as yup from "yup";


import {useCreateImportTask} from "../query";
import WorkgroupAutocomplete from "../../../components/inputs/WorkgroupsAutocomplete/WorkgroupAutocomplete";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import MDBox from "materialDashboard/components/MDBox";
import MDTypography from "materialDashboard/components/MDTypography";
import MDButton from "materialDashboard/components/MDButton";
import MDInput from "materialDashboard/components/MDInput";

const GroupCreate = () => {
    const {t} = useTranslation(['importTasks', 'validation']);

    const importTaskMutation = useCreateImportTask();

    const formik = useFormik({
        initialValues: {
            format: "MOD1-CSV",
            file: null,
            workgroupId: "",
            overwrite: false,
        },
        validationSchema: yup.object({
            file: yup.mixed().required(t('validation:required')),
            workgroupId: yup.string().required(t('validation:required')),
            overwrite: yup.boolean().required(t('validation:required')),
        }),
        onSubmit: async (values) => {
            await importTaskMutation.mutateAsync(values);
        }
    });

    const toggleOverwrite = () => {
        formik.setFieldValue("overwrite", !formik.values.overwrite);
    }

    const isMod23 = formik.values.format === "MOD23-CAAMLv6" || formik.values.format === "MOD23-X23";

    useEffect(() => {
        if (isMod23) {
            formik.setFieldValue("overwrite", false);
        }
    }, [isMod23]);

    return <Grid container justifyContent="center" alignItems="center" mt={4}>
        <Grid item xs={12} lg={8}>
            <Card>
                <MDBox
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    mx={2}
                    mt={-3}
                    p={2}
                    mb={1}
                    textAlign="center"
                >
                    <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                        {t('importTaskCreate.title')}
                    </MDTypography>
                </MDBox>

                <Stack direction="column" spacing={3} p={3}>
                    <MDBox>
                        <MDTypography variant="h6" fontWeight="medium">
                            {t('importTaskCreate.format')}
                        </MDTypography>
                        <FormControl>
                            <RadioGroup
                                name="format"
                                value={formik.values.format}
                                onChange={formik.handleChange}
                            >
                                <Grid container>
                                    <Grid item xs={12} lg={6}>
                                        <FormControlLabel value="MOD1-CSV" control={<Radio/>} label={<MDBox>
                                            <MDTypography variant="body2">
                                                MODELLO1 - YETI CSV v2025
                                            </MDTypography>
                                        </MDBox>}/>
                                        {/*<FormControlLabel value="MOD1-TXT" control={<Radio/>} label={<MDBox>*/}
                                        {/*    <MDTypography variant="body2">*/}
                                        {/*        MODELLO1 - YETI TXT*/}
                                        {/*    </MDTypography>*/}
                                        {/*    <MDTypography variant="caption" color="warning">*/}
                                        {/*        Deprecato a partire dal 1 settembre 2026*/}
                                        {/*    </MDTypography>*/}
                                        {/*</MDBox>}/>*/}
                                        {/*<FormControlLabel value="MOD1-HUSKY-CSV" control={<Radio/>} label={<MDBox>*/}
                                        {/*    <MDTypography variant="body2">*/}
                                        {/*        MODELLO1 - HUSKY CSV*/}
                                        {/*    </MDTypography>*/}
                                        {/*    <MDTypography variant="caption" color="warning">*/}
                                        {/*        Deprecato a partire dal 1 settembre 2026*/}
                                        {/*    </MDTypography>*/}
                                        {/*</MDBox>}/>*/}
                                    </Grid>
                                    <Grid item xs={12} lg={6}>
                                        <FormControlLabel value="MOD23-CAAMLv6" control={<Radio/>} label={<MDBox>
                                            <MDTypography variant="body2">
                                                MODELLO 2/3 - CAAMLv6
                                            </MDTypography>
                                        </MDBox>}/>
                                        <FormControlLabel value="MOD23-X23" control={<Radio/>} label={<MDBox>
                                            <MDTypography variant="body2">
                                                MODELLO 2/3 - YETI X23
                                            </MDTypography>
                                        </MDBox>}/>
                                    </Grid>
                                </Grid>
                            </RadioGroup>
                        </FormControl>
                    </MDBox>

                    <MDBox>
                        <MDTypography variant="h6" fontWeight="medium">
                            {t('importTaskCreate.file')}
                        </MDTypography>

                        <MDBox>
                            <MDInput type="file"
                                     name="file"
                                     variant="standard"
                                     fullWidth
                                     onChange={(e) => {
                                         formik.setFieldValue("file", e.currentTarget.files[0]);
                                     }}
                                     error={formik.touched.file && Boolean(formik.errors.file)}
                                     helperText={formik.touched.file && formik.errors.file}
                            />
                        </MDBox>

                        <WorkgroupAutocomplete onChange={value => formik.setFieldValue("workgroupId", value)}
                                               value={formik.values.workgroupId}
                                               workgroupFilters={{
                                                   acl__data_write: true,
                                               }}
                                               MDInputProps={
                                                   {
                                                       error: formik.touched.workgroupId && Boolean(formik.errors.workgroupId),
                                                       helperText: formik.touched.workgroupId && formik.errors.workgroupId,
                                                   }}
                        />

                        <FormGroup>
                            <FormControlLabel control={<Switch checked={formik.values.overwrite}
                                                               disabled={isMod23}
                                                               onClick={toggleOverwrite}/>}
                                              label={t('importTaskCreate.overwrite')}/>
                        </FormGroup>
                    </MDBox>

                    <Stack justifyContent="flex-end">
                        <MDButton variant="gradient" color="info" onClick={formik.handleSubmit} sx={{ml: "auto"}}>
                            {t('importTaskCreate.submit')}
                        </MDButton>
                    </Stack>

                </Stack>
            </Card>
        </Grid>
    </Grid>
}

export default GroupCreate;